// src/containers/Receptions/ReceptionList/hooks/useReceptionFilters.ts
import { useState, useCallback } from "react";
import { useSnackbar } from "$utils/useSnackbar";
import qs from "qs";
import { endOfDay } from "date-fns";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";
import { RECEPTION_SYNCHRONIZED_STATUS_FILTER } from "../types";
import { Reception } from "$types";
import { JobType } from "$types/Job";
import { useJobs } from "$hooks/useJobs";
import useReceptionContext from "./useReceptionContext";

const useReceptionFilters = (
  filters: any,
  setFilter: (key: string, value: any) => void
) => {
  const { defaultOptions } = useReceptionContext();
  const { startJob } = useJobs();
  const [openErrorSnackbar] = useSnackbar(snackbarErrorOptions);
  const [dateStart_tfilter, setDateStart_tfilter] = useState<Date | null>(null);
  const [dateEnd_tfilter, setDateEnd_tfilter] = useState<Date | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedRowsReception, setSelectedRowsReception] = useState<
    Reception[]
  >([]);
  const [synchronization_tfilter, setSynchronization_tfilter] =
    useState<RECEPTION_SYNCHRONIZED_STATUS_FILTER | null>(null);

  const onDateChange = useCallback(
    (dates: any) => {
      if (Array.isArray(dates)) {
        const [start, end] = dates;
        setDateStart_tfilter(start);
        setDateEnd_tfilter(end);

        if (!start && !end) {
          setDateStart_tfilter(null);
          setDateEnd_tfilter(null);
          setFilter("receptionDate", undefined);
        } else if (start) {
          const effectiveEnd = end || start;
          const startIso = start.toISOString();
          const endIso = endOfDay(effectiveEnd).toISOString();

          setFilter("receptionDate", {
            $gte: startIso,
            $lte: endIso,
          });
        }
      }
    },
    [setDateEnd_tfilter, setDateStart_tfilter, setFilter]
  );

  const onDatePickerClose = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      // Si une seule date est sélectionnée à la fermeture du picker
      if (start && !end) {
        setDateStart_tfilter(start);
        setDateEnd_tfilter(start);

        const startIso = start.toISOString();
        const endIso = endOfDay(start).toISOString();

        setFilter("receptionDate", {
          $gte: startIso,
          $lte: endIso,
        });
      }
    },
    [setDateEnd_tfilter, setDateStart_tfilter, setFilter]
  );

  /**
   * Build Prisma format query string from filters
   */
  const buildPrismaQuery = (
    selectedIds: number[] = [],
    pageSize = 100000
  ): string => {
    const query: Record<string, any> = {
      page: 1,
      rowsPerPage: pageSize,
    };

    // If specific receptions are selected, filter by IDs
    if (selectedIds.length > 0) {
      query.id = selectedIds.join(",");
    } else {
      // Otherwise, use current filters
      // Transform filters to Prisma format
      if (filters.company) {
        const { company } = filters;
        if (
          typeof company === "object" &&
          company !== null &&
          "$eq" in company
        ) {
          query.company = company.$eq;
        } else if (typeof company === "string") {
          query.company = company;
        }
      }
      if (filters.status) {
        const { status } = filters;
        if (
          typeof status === "object" &&
          status !== null &&
          "$in" in status &&
          Array.isArray(status.$in)
        ) {
          query.status = status.$in.join(",");
        } else if (typeof status === "string") {
          query.status = status;
        }
      }
      if (filters.provider) {
        const { provider } = filters;
        if (
          typeof provider === "object" &&
          provider !== null &&
          "$eq" in provider
        ) {
          query.provider = provider.$eq;
        } else if (typeof provider === "string") {
          query.provider = provider;
        }
      }
      if (filters.warehouse) {
        const { warehouse } = filters;
        if (
          typeof warehouse === "object" &&
          warehouse !== null &&
          "$in" in warehouse &&
          Array.isArray(warehouse.$in)
        ) {
          query.warehouse = warehouse.$in.join(",");
        } else if (typeof warehouse === "string") {
          query.warehouse = warehouse;
        }
      }
      // Note: receptionDate filters might need special handling if needed
    }

    // Add sort
    if (defaultOptions.defaultSort) {
      const sortValue =
        typeof defaultOptions.defaultSort === "string"
          ? defaultOptions.defaultSort
          : typeof defaultOptions.defaultSort === "object" &&
              defaultOptions.defaultSort !== null &&
              "field" in defaultOptions.defaultSort &&
              "direction" in defaultOptions.defaultSort
            ? `${
                (
                  defaultOptions.defaultSort as {
                    field: string;
                    direction: string;
                  }
                ).field
              }:${
                (
                  defaultOptions.defaultSort as {
                    field: string;
                    direction: string;
                  }
                ).direction
              }`
            : undefined;
      if (sortValue) {
        query.sort = sortValue;
      }
    }

    return qs.stringify(query);
  };

  const exportReceptions = async () => {
    setIsExporting(true);

    try {
      const selectedReceptionsIds = selectedRowsReception.map((o) => o.id);
      // Build Prisma format query
      const prismaQuery = buildPrismaQuery(selectedReceptionsIds, 100000);

      await startJob({
        type: JobType.EXPORT_RECEPTIONS,
        label: "Export réceptions",
        params: { query: prismaQuery },
      });
    } catch (error) {
      console.error("Error exporting receptions:", error);
      openErrorSnackbar("Failed to export receptions");
    } finally {
      setIsExporting(false);
    }
  };

  return {
    dateStart_tfilter,
    dateEnd_tfilter,
    setDateStart_tfilter,
    setDateEnd_tfilter,
    onDateChange,
    onDatePickerClose,
    synchronization_tfilter,
    setSynchronization_tfilter,
    exportReceptions,
    isExporting,
    selectedRowsReception,
    setSelectedRowsReception,
  };
};

export default useReceptionFilters;
